export const thinsp = '\u2009'; // thin space
export const emdash = '\u2014'; // em dash
export const superscriptPlus = '\u207A'; // superscript plus


export function prettyDecimal(number, digits = 2, locale = 'en-US') {
  if (!Number.isFinite(number)) return 'n/a';

  return number.toLocaleString(locale, { maximumFractionDigits: digits });
}

/**
 * Suffixes used for pretty printing of time values in milliseconds.
 */
const msSuffixes = ['ms', 'ns', 'μs', 'ms', 's'];
/**
 * Scales used for pretty printing of time values in milliseconds.
 */
const msScales = [0, 1e6, 1e3, 1, 1e-3];

/**
 * Prints given milliseconds in an appropriate seconds-based time unit and fixed number of decimal places.
 * ```
 * prettyPrintMilliseconds(0.03277); // returns '32.770μs'
 * prettyPrintMilliseconds(47738102); // returns '07:42:18:900'
 * ```
 * @private
 *
 * @param {number} milliseconds - Number of milliseconds as floating point number.
 */
export function prettyPrintMilliseconds(milliseconds) {
  if (!Number.isFinite(milliseconds)) return 'n/a';

  if (milliseconds < 120_000) {
    let prefix = milliseconds > 0 ? Math.max(1, Math.floor(Math.log(milliseconds * 10) / Math.log(1e3)) + 3) : 0;
    prefix = Math.max(0, Math.min(4, prefix));

    const value = milliseconds * msScales[prefix];
    return `${value.toFixed(prefix < 4 ? 2 : 3)}${thinsp + msSuffixes[prefix]}`;
  }

  const date = new Date(milliseconds);
  const hh = Math.floor(milliseconds / 3_600_000).toString().padStart(2, 0);
  const mm = date.getMinutes().toString().padStart(2, 0);
  const ss = date.getSeconds().toString().padStart(2, 0);
  const ms = date.getMilliseconds().toString().padStart(3, 0);
  return `${hh > 0 ? hh + ':' : ''}${mm}:${ss}:${ms}`;
}

/**
 * Prints given ratio as a percentage with fixed number of decimal places.
 * @private
 */
export function prettyPrintPercentage(ratio, fractionDigits = 2) {
  if (!Number.isFinite(ratio)) return 'n/a';

  return `${(ratio * 100).toFixed(fractionDigits)}${thinsp}%`;
}

const byteSuffixes = ['', 'Ki', 'Mi', 'Gi', 'Ti', 'Pi', 'Ei', 'Zi', 'Yi'];
const oneOverLog1024 = 1.0 / Math.log(1024);

/**
 * Converts a number of bytes to a human-readable string with an appropriate byte suffix
 * based on the  International System of Quantities (ISQ, ISO/IEC 80000).
 * @private
 *
 * @param {number} bytes - The number of bytes to format.
 * @param {number} [prefix] - The optional exponent of the 1024 base to use for the byte suffix. If not
 *      provided, a suitable prefix is chosen automatically based on the size of the value.
 * @returns {string} A formatted string representing the number of bytes with an appropriate byte suffix,
 *      e.g., "1.23MiB".
 */
export function prettyPrintBytes(bytes, prefix = undefined) {
  if (!Number.isFinite(bytes)) return 'n/a';
  if (prefix === undefined) {
    prefix = bytes > 0 ? Math.floor(Math.log(bytes) * oneOverLog1024) : 0;
  } else {
    prefix = Math.min(byteSuffixes.length - 1, Math.max(0, prefix));
  }
  const value = bytes / 1024 ** prefix;

  if (prefix === 0) {
    // If it is an integer, don't show decimal places, also use 'Bytes' instead of 'B'
    return `${Number.isInteger(value) ? value : value.toFixed(4)}${thinsp}Bytes`;
  }
  return `${value.toFixed(3)}${thinsp + byteSuffixes[prefix]}B`;
}